import { UIDiv, UIText } from '@ui';

class TooltipClass extends UIDiv {
  constructor() {
    super();
    this.setClass(
      'fixed bg-black/80 text-white px-2.5 py-1.5 rounded text-xs z-[1000] pointer-events-none invisible opacity-0 transition-opacity duration-200',
    );
    document.body.appendChild(this.dom);

    this.currentTarget = null;
    this.isVisible = false;
    this.hideOnClick = false;
    this.hideOnScroll = false;
    this.hideTimeout = null;

    // Bind methods to preserve context
    this._handleGlobalClick = this._handleGlobalClick.bind(this);
    this._handleGlobalScroll = this._handleGlobalScroll.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
  }

  /**
   * Shows and positions the tooltip with the provided content.
   * @param {HTMLElement} targetElement The element the tooltip should anchor to.
   * @param {string} content The text to display in the tooltip.
   * @param {string} position The placement ('top', 'bottom', 'left', 'right').
   * @param {Object} options Additional options for tooltip behavior.
   * @param {boolean} options.hideOnClick Whether to hide tooltip when clicking elsewhere.
   * @param {boolean} options.hideOnScroll Whether to hide tooltip when scrolling.
   * @param {number} options.delay Delay in ms before adding global event listeners.
   */
  show(targetElement, content, position = 'bottom', options = {}) {
    if (!targetElement || !content) return;

    // Clean up previous state completely
    clearTimeout(this.hideTimeout);
    this.#removeGlobalListeners();

    // Store current state
    this.currentTarget = targetElement;
    this.hideOnClick = options.hideOnClick ?? false;
    this.hideOnScroll = options.hideOnScroll ?? false;
    const delay = options.delay ?? 10;

    // Set the new content
    this.clear();
    this.add(new UIText(content));

    this.#updatePosition(targetElement, position);

    this.setStyle('visibility', ['visible']);
    this.setStyle('opacity', ['1']);
    this.isVisible = true;

    if (this.hideOnClick || this.hideOnScroll) {
      this.hideTimeout = setTimeout(() => {
        this.#addGlobalListeners();
      }, delay);
    }
  }

  /**
   * Hides the tooltip.
   */
  hide() {
    if (!this.isVisible) return;

    this.setStyle('visibility', ['hidden']);
    this.setStyle('opacity', ['0']);
    this.isVisible = false;
    this.currentTarget = null;

    // Reset configuration state
    this.hideOnClick = false;
    this.hideOnScroll = false;

    // Clear timeout and remove global listeners
    clearTimeout(this.hideTimeout);
    this.#removeGlobalListeners();
  }

  /**
   * Private method to calculate and set the tooltip's position.
   */
  #updatePosition(element, position) {
    const elRect = element.getBoundingClientRect();
    // We get the tooltip's rect *after* its content has been updated
    const tipRect = this.dom.getBoundingClientRect();

    let top, left;

    switch (position) {
      case 'top':
        top = elRect.top - tipRect.height - 5;
        left = elRect.left + elRect.width / 2 - tipRect.width / 2;
        break;
      case 'left':
        top = elRect.top + elRect.height / 2 - tipRect.height / 2;
        left = elRect.left - tipRect.width - 5;
        break;
      case 'right':
        top = elRect.top + elRect.height / 2 - tipRect.height / 2;
        left = elRect.right + 5;
        break;
      default: // 'bottom'
        top = elRect.bottom + 5;
        left = elRect.left + elRect.width / 2 - tipRect.width / 2;
        break;
    }

    this.setStyle('top', [`${top}px`]);
    this.setStyle('left', [`${left}px`]);
  }

  /**
   * Add global event listeners for hiding tooltip
   */
  #addGlobalListeners() {
    if (this.hideOnClick) {
      document.addEventListener('click', this._handleGlobalClick);
      document.addEventListener('keydown', this._handleKeydown);
    }
    if (this.hideOnScroll) {
      document.addEventListener('scroll', this._handleGlobalScroll, true);
      document.addEventListener('click', this._handleGlobalClick);
      window.addEventListener('resize', this._handleGlobalScroll);
    }
  }

  /**
   * Remove global event listeners
   */
  #removeGlobalListeners() {
    document.removeEventListener('click', this._handleGlobalClick);
    document.removeEventListener('keydown', this._handleKeydown);
    document.removeEventListener('scroll', this._handleGlobalScroll, true);
    window.removeEventListener('resize', this._handleGlobalScroll);
  }

  /**
   * Handle global click events
   */
  _handleGlobalClick(event) {
    if (!this.isVisible || !this.currentTarget) return;

    // Don't hide if clicking on the target element itself
    // Check both contains and direct equality for better mobile support
    if (
      event.target === this.currentTarget ||
      (this.currentTarget.contains && this.currentTarget.contains(event.target))
    ) {
      return;
    }

    // Don't hide if clicking on the tooltip itself (though it should be pointer-events-none)
    if (this.dom.contains(event.target)) {
      return;
    }

    this.hide();
  }

  /**
   * Handle global scroll and resize events
   */
  _handleGlobalScroll(event) {
    if (!this.isVisible) return;

    // Hide on scroll
    this.hide();
  }

  /**
   * Handle keyboard events
   */
  _handleKeydown(event) {
    if (!this.isVisible) return;

    // Hide on Escape key
    if (event.key === 'Escape') {
      this.hide();
    }
  }

  /**
   * Convenience method for showing tooltip with click and scroll hiding enabled
   * @param {HTMLElement} targetElement The element the tooltip should anchor to
   * @param {string} content The text to display in the tooltip
   * @param {string} position The placement ('top', 'bottom', 'left', 'right')
   */
  showWithAutoHide(targetElement, content, position = 'bottom') {
    return this.show(targetElement, content, position, {
      hideOnClick: true,
      hideOnScroll: true,
      delay: 100, // Slightly longer delay for mobile
    });
  }

  /**
   * Toggle tooltip visibility (useful for mobile click interactions)
   * @param {HTMLElement} targetElement The element the tooltip should anchor to
   * @param {string} content The text to display in the tooltip
   * @param {string} position The placement ('top', 'bottom', 'left', 'right')
   */
  toggleWithAutoHide(targetElement, content, position = 'bottom') {
    // If tooltip is visible and targeting the same element, hide it
    if (this.isVisible && this.currentTarget === targetElement) {
      this.hide();
    } else {
      // Otherwise show the tooltip
      this.showWithAutoHide(targetElement, content, position);
    }
  }
}

// Export a single instance, just like your Popover
const Tooltip = new TooltipClass();
export default Tooltip;
